import { useState, useEffect, useCallback } from "react";
import { getAdminAnalytics } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Users, UserCheck, FileText, UserPlus, TrendingUp, BarChart3 } from "lucide-react";

function formatPct(n) {
  if (n == null || isNaN(n)) return "-";
  return `${(n * 100).toFixed(1)}%`;
}

const DEFAULT_ANALYTICS = {
  total_users: 0,
  users_by_plan: {},
  users_by_billing_role: {},
  active_vs_inactive_users: { active: 0, inactive: 0 },
  consultations_count: 0,
  patients_count: 0,
  trial_to_paid_conversion: null,
  monthly_growth: [],
};

function MonthlyChart({ data }) {
  if (!data || !data.length) {
    return (
      <div className="h-48 flex items-center justify-center text-slate-400 text-sm border rounded-lg border-dashed">
        Sin datos de crecimiento mensual
      </div>
    );
  }
  const maxVal = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-48 pt-4">
      {data.slice(0, 12).map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full bg-sky-200 rounded-t min-h-[4px] transition-all"
            style={{ height: `${Math.max(4, (d.count / maxVal) * 100)}%` }}
          />
          <span className="text-[10px] text-slate-500 truncate max-w-full">{d.month}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminAnalytics();
      const d = res?.data;
      setData(d && typeof d === "object" ? d : DEFAULT_ANALYTICS);
    } catch (err) {
      setData(DEFAULT_ANALYTICS);
      if (err.response?.status !== 404) {
        toast.error("Error al cargar analytics. Algunos datos pueden estar incompletos.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const byPlan = data?.users_by_plan || {};
  const byRole = data?.users_by_billing_role || {};
  const activeInactive = data?.active_vs_inactive_users || { active: 0, inactive: 0 };
  const monthlyGrowth = data?.monthly_growth || [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Analytics</h1>
        <p className="text-slate-500 mt-1">Métricas de negocio y rendimiento SaaS</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total usuarios</p>
                <p className="text-2xl font-bold text-slate-800">{data?.total_users ?? 0}</p>
              </div>
              <Users className="h-10 w-10 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Usuarios activos</p>
                <p className="text-2xl font-bold text-emerald-600">{activeInactive.active ?? 0}</p>
              </div>
              <UserCheck className="h-10 w-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Consultas totales</p>
                <p className="text-2xl font-bold text-slate-800">{data?.consultations_count ?? 0}</p>
              </div>
              <FileText className="h-10 w-10 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pacientes totales</p>
                <p className="text-2xl font-bold text-slate-800">{data?.patients_count ?? 0}</p>
              </div>
              <UserPlus className="h-10 w-10 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Usuarios por plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(byPlan).length === 0 ? (
              <p className="text-slate-500 text-sm">Sin datos</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(byPlan).map(([plan, count]) => (
                  <Badge key={plan} variant="outline" className="text-sm">
                    {plan}: {count}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuarios por rol
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(byRole).length === 0 ? (
              <p className="text-slate-500 text-sm">Sin datos</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(byRole).map(([role, count]) => (
                  <Badge key={role} variant="outline" className="text-sm">
                    {role}: {count}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Activos vs inactivos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-emerald-500" />
                <span className="text-sm">Activos: {activeInactive.active ?? 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-slate-400" />
                <span className="text-sm">Inactivos: {activeInactive.inactive ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Trial → Pagado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-slate-800">
              {formatPct(data?.trial_to_paid_conversion)}
            </p>
            <p className="text-xs text-slate-500 mt-1">Tasa de conversión estimada</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Crecimiento mensual (registros)</CardTitle>
        </CardHeader>
        <CardContent>
          <MonthlyChart data={monthlyGrowth} />
        </CardContent>
      </Card>
    </div>
  );
}
